import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Calendar } from "primereact/calendar";
import Lottie from "lottie-react";
import {
  AsYouType,
  getCountryCallingCode,
  Metadata,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import countryList from "react-select-country-list";
import { CircleFlag } from "react-circle-flags";
import "./KycMobileClientSections.css";
import { MOBILE_TEXT_ENTRY_PROPS } from "./mobileTextEntryProps";
import { countryMatchesSearch } from "../../../utils/iso2to3";
import MobileIndividualNamePreview from "./MobileIndividualNamePreview";

import iconPassport from "../../../images/passport.svg";
import iconDateOfBirth from "../../../images/birthday.svg";
import iconAddress from "../../../images/address.svg";
import iconEmail from "../../../images/email_icon.svg";
import iconPhoneCall from "../../../images/phone-call.svg";
import iconPhoneBlue from "../../../images/phone-blue.svg";
import iconBusinessActivity from "../../../images/briefcaseX.svg";
import iconNameJson from "../../../images/individual-lottie.json";
import iconEntityNameJson from "../../../images/building-lottie.json";

const COUNTRY_OPTIONS = countryList().getData();

function readMapboxToken() {
  const raw =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
    "";

  return String(raw).trim().replace(/^["']|["']$/g, "");
}

const MAPBOX_TOKEN = readMapboxToken();

function toDateOrNull(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDate(v) {
  if (!v) return "";
  try {
    const d = toDateOrNull(v);
    if (!d || Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return "";
  }
}

function countryCodeToName(code) {
  const c = String(code || "")
    .trim()
    .toUpperCase();
  if (!c) return "";

  try {
    const dn = new Intl.DisplayNames(["en"], { type: "region" });
    return dn.of(c) || c;
  } catch {
    return c;
  }
}

function getContextEntry(feature, prefix) {
  const list = Array.isArray(feature?.context) ? feature.context : [];
  return list.find((item) => String(item?.id || "").startsWith(`${prefix}.`));
}

function toCountryCode(value) {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (!raw) return "";
  if (raw.length === 2) return raw;
  return raw.split("-").pop() || raw;
}

function parseMapboxFeature(feature) {
  if (!feature) return null;

  const countryCtx = getContextEntry(feature, "country");
  const placeCtx =
    getContextEntry(feature, "place") ||
    getContextEntry(feature, "locality") ||
    getContextEntry(feature, "district");

  const houseNumber = feature?.address ? `${feature.address} ` : "";
  const streetName =
    feature?.text ||
    feature?.properties?.address ||
    feature?.place_name?.split(",")?.[0] ||
    "";
  const firstLine = `${houseNumber}${streetName}`.trim() || feature?.place_name || "";

  return {
    id: String(feature.id || firstLine),
    label: feature.place_name || firstLine,
    address: firstLine,
    city: placeCtx?.text || "",
    country: toCountryCode(countryCtx?.short_code || countryCtx?.text || ""),
  };
}

function parseMapboxCityFeature(feature) {
  if (!feature) return null;

  const countryCtx = getContextEntry(feature, "country");
  const cityName =
    feature?.text ||
    feature?.place_name?.split(",")?.[0] ||
    feature?.properties?.name ||
    "";

  if (!cityName) return null;

  return {
    id: String(feature.id || cityName),
    city: cityName,
    country: toCountryCode(countryCtx?.short_code || countryCtx?.text || ""),
    label: feature.place_name || cityName,
  };
}

function isValidEmail(value) {
  const v = String(value || "").trim();
  if (!v) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function isValidWebsite(value) {
  const v = String(value || "").trim();
  if (!v) return true;
  try {
    const normalized = /^https?:\/\//i.test(v) ? v : `https://${v}`;
    const url = new URL(normalized);
    return Boolean(url.hostname && url.hostname.includes("."));
  } catch {
    return false;
  }
}

function sanitizePhone(value) {
  return String(value || "").replace(/[^0-9+() ]/g, "");
}

function capitalizeFirstLetter(value) {
  return String(value || "").replace(/^(\s*)([a-z])/, (_, spaces, letter) => (
    `${spaces}${letter.toUpperCase()}`
  ));
}

function hasCountryOption(code) {
  const normalized = String(code || "")
    .trim()
    .toUpperCase();
  return COUNTRY_OPTIONS.some((opt) => opt.value === normalized);
}

function getPhoneCountryCode(countryCode) {
  const normalized = String(countryCode || "")
    .trim()
    .toUpperCase();
  if (!normalized || !hasCountryOption(normalized)) return "";
  try {
    return `+${getCountryCallingCode(normalized)}`;
  } catch {
    return "";
  }
}

function formatPhoneDraft(nationalDigits, countryCode) {
  const normalizedCountry = String(countryCode || "")
    .trim()
    .toUpperCase();
  const digits = String(nationalDigits || "").replace(/\D/g, "");
  const callingCode = getPhoneCountryCode(normalizedCountry);
  if (!callingCode) return digits;
  if (!digits) return callingCode;

  try {
    return new AsYouType(normalizedCountry).input(`${callingCode}${digits}`);
  } catch {
    return `${callingCode} ${digits}`.trim();
  }
}

function getPreferredPhoneCountry(customer) {
  const savedCountry = String(customer?.phoneCountry || "")
    .trim()
    .toUpperCase();
  if (hasCountryOption(savedCountry)) return savedCountry;

  const existingPhone = String(customer?.phone || "").trim();
  if (existingPhone) {
    try {
      const parsed = parsePhoneNumberFromString(existingPhone);
      const parsedCountry = String(parsed?.country || "")
        .trim()
        .toUpperCase();
      if (hasCountryOption(parsedCountry)) return parsedCountry;
    } catch {}
  }

  const domicileCountry = String(customer?.country || "")
    .trim()
    .toUpperCase();
  if (hasCountryOption(domicileCountry)) return domicileCountry;

  const nationalityCountry = String(
    customer?.nationalityOrIncorporationCountry || ""
  )
    .trim()
    .toUpperCase();
  if (hasCountryOption(nationalityCountry)) return nationalityCountry;

  return "AE";
}

function getNationalDigitsFromPhone(value, countryCode) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const parsed = parsePhoneNumberFromString(raw);
    if (parsed?.nationalNumber) return String(parsed.nationalNumber);
  } catch {}

  const digitsOnly = raw.replace(/\D/g, "");
  if (!digitsOnly) return "";

  const callingCode = getPhoneCountryCode(countryCode).replace(/\D/g, "");
  if (callingCode && digitsOnly.startsWith(callingCode)) {
    return digitsOnly.slice(callingCode.length);
  }
  return digitsOnly;
}

function capNationalDigitsForCountry(nationalDigits, countryCode) {
  const normalizedCountry = String(countryCode || "")
    .trim()
    .toUpperCase();
  const digits = String(nationalDigits || "").replace(/\D/g, "");
  if (!normalizedCountry || !digits) return digits;

  try {
    const metadata = new Metadata();
    metadata.selectNumberingPlan(normalizedCountry);
    const possibleLengths = Array.isArray(metadata.possibleLengths())
      ? metadata.possibleLengths()
          .map((len) => Number(len))
          .filter((len) => Number.isFinite(len) && len > 0)
          .sort((a, b) => a - b)
      : [];

    if (!possibleLengths.length) return digits;

    const maxLength = possibleLengths[possibleLengths.length - 1];
    return digits.slice(0, maxLength);
  } catch {
    return digits;
  }
}

function RequiredFieldError() {
  return (
    <div className="kycMobileFieldError">
      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
      This field is required
    </div>
  );
}

function autoResizeTextarea(el) {
  if (!el) return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight * 3 + verticalExtras;
  const maxHeight = lineHeight * 10 + verticalExtras;

  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${nextHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

const BUSINESS_ACTIVITY_MIN_HEIGHT = "calc(1.45em * 3 + 1.1rem)";

export default function KycMobileCustomerDetails({
  form,
  separateNames = false,
  onSetAt,
  readOnly = false,
  continueAttempted = false,
  useMainCountryOfOperation = false,
}) {
  const customer = form?.customer || {};
  const isEntity = customer.type === "entity";
  const showMainCountryOfOperation = isEntity && useMainCountryOfOperation;
  const [dobSheetOpen, setDobSheetOpen] = useState(false);
  const [draftDob, setDraftDob] = useState(null);
  const [dobSelectionStage, setDobSelectionStage] = useState("year");
  const [dobViewDate, setDobViewDate] = useState(new Date());
  const [nationalitySheetOpen, setNationalitySheetOpen] = useState(false);
  const [operationCountrySheetOpen, setOperationCountrySheetOpen] = useState(false);
  const [countrySheetOpen, setCountrySheetOpen] = useState(false);
  const [phonePadOpen, setPhonePadOpen] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("");
  const [phoneCountrySheetOpen, setPhoneCountrySheetOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [websiteTouched, setWebsiteTouched] = useState(false);
  const industryTypingTimerRef = useRef(null);
  const industryBackupRef = useRef(null);
  const lastAssessedTextRef = useRef("");
  const addressTimerRef = useRef(null);
  const addressAbortRef = useRef(null);
  const cityTimerRef = useRef(null);
  const cityAbortRef = useRef(null);
  const addressBoxRef = useRef(null);
  const cityBoxRef = useRef(null);
  const businessActivityRef = useRef(null);
  const countrySearchRef = useRef(null);
  const countryOptions = COUNTRY_OPTIONS;

  useEffect(() => {
    return () => {
      if (industryTypingTimerRef.current) {
        clearTimeout(industryTypingTimerRef.current);
      }
      if (addressTimerRef.current) {
        clearTimeout(addressTimerRef.current);
      }
      if (addressAbortRef.current) {
        addressAbortRef.current.abort();
      }
      if (cityTimerRef.current) {
        clearTimeout(cityTimerRef.current);
      }
      if (cityAbortRef.current) {
        cityAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    function onPointerDown(e) {
      if (!addressBoxRef.current?.contains(e.target)) {
        setAddressDropdownOpen(false);
      }
      if (!cityBoxRef.current?.contains(e.target)) {
        setCityDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, []);

  useEffect(() => {
    const assessed = String(
      form?.internalUse?.industryRiskResult?.auto?.checkedText || ""
    ).trim();
    if (!assessed) return;
    if (assessed !== lastAssessedTextRef.current) {
      industryBackupRef.current = null;
      lastAssessedTextRef.current = assessed;
    }
  }, [form?.internalUse?.industryRiskResult?.auto?.checkedText]);

  useEffect(() => {
    if (!isEntity) return;

    const country = String(customer.country || "").trim();
    const incorporationCountry = String(
      customer.nationalityOrIncorporationCountry || ""
    ).trim();

    if (country && incorporationCountry !== country) {
      setCustomer(["nationalityOrIncorporationCountry"], country);
      return;
    }

    if (!country && incorporationCountry) {
      setCustomer(["country"], incorporationCountry);
    }
  }, [
    isEntity,
    customer.country,
    customer.nationalityOrIncorporationCountry,
  ]);

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      autoResizeTextarea(businessActivityRef.current);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [customer.businessActivity]);

  useEffect(() => {
    if (
      !nationalitySheetOpen &&
      !operationCountrySheetOpen &&
      !countrySheetOpen &&
      !phoneCountrySheetOpen
    )
      return;

    if (
      phoneCountrySheetOpen &&
      !nationalitySheetOpen &&
      !operationCountrySheetOpen &&
      !countrySheetOpen
    ) {
      const timer = window.setTimeout(() => {
        countrySearchRef.current?.focus({ preventScroll: true });
        countrySearchRef.current?.select?.();
      }, 360);
      return () => {
        window.clearTimeout(timer);
      };
    }

    const frame = window.requestAnimationFrame(() => {
      countrySearchRef.current?.focus();
      countrySearchRef.current?.select?.();
    });
    const timer = window.setTimeout(() => {
      countrySearchRef.current?.focus();
      countrySearchRef.current?.select?.();
    }, 120);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [nationalitySheetOpen, operationCountrySheetOpen, countrySheetOpen, phoneCountrySheetOpen]);

  function syncIndustryAssessmentWithTyping(nextVal) {
    const current = String(nextVal || "").trim();
    const assessedTextLive = String(
      form?.internalUse?.industryRiskResult?.auto?.checkedText || ""
    ).trim();
    const baselineAssessedText =
      assessedTextLive || lastAssessedTextRef.current;

    if (!baselineAssessedText) return;

    const saved = form?.internalUse?.industryRiskResult;

    if (current === baselineAssessedText) {
      if (saved === null && industryBackupRef.current) {
        onSetAt?.(
          ["internalUse", "industryRiskResult"],
          industryBackupRef.current
        );
      }
      return;
    }

    if (saved !== null) {
      industryBackupRef.current = saved;
      onSetAt?.(["internalUse", "industryRiskResult"], null);
    }
  }

  function debouncedSyncIndustryAssessment(nextVal) {
    if (industryTypingTimerRef.current) {
      clearTimeout(industryTypingTimerRef.current);
    }
    industryTypingTimerRef.current = setTimeout(() => {
      syncIndustryAssessmentWithTyping(nextVal);
    }, 250);
  }

  function setCustomer(pathTail, value) {
    onSetAt?.(["customer", ...pathTail], value);
  }

  function openDobSheet() {
    if (readOnly) return;
    const existingDate = toDateOrNull(customer.dobOrIncorporationDate);
    setDraftDob(existingDate);
    setDobViewDate(existingDate || new Date());
    setDobSelectionStage("year");
    setDobSheetOpen(true);
  }

  function openNationalitySheet() {
    if (readOnly) return;
    setCountrySearch("");
    setNationalitySheetOpen(true);
  }

  function openOperationCountrySheet() {
    if (readOnly) return;
    setCountrySearch("");
    setOperationCountrySheetOpen(true);
  }

  function openCountrySheet() {
    if (readOnly) return;
    setCountrySearch("");
    setCountrySheetOpen(true);
  }

  function closeDobSheet() {
    setDobSheetOpen(false);
  }

  function closeNationalitySheet() {
    setNationalitySheetOpen(false);
  }

  function closeOperationCountrySheet() {
    setOperationCountrySheetOpen(false);
  }

  function closeCountrySheet() {
    setCountrySheetOpen(false);
  }

  function openPhonePad() {
    if (readOnly) return;
    const nextPhoneCountry = getPreferredPhoneCountry(customer);
    setPhoneCountry(nextPhoneCountry);
    setPhoneDraft(
      capNationalDigitsForCountry(
        getNationalDigitsFromPhone(customer.phone, nextPhoneCountry),
        nextPhoneCountry
      )
    );
    setPhonePadOpen(true);
  }

  function closePhonePad() {
    setPhonePadOpen(false);
    setPhoneDraft("");
    setPhoneCountry("");
    setPhoneCountrySheetOpen(false);
  }

  function applyPhonePad() {
    const nextPhone = formatPhoneDraft(phoneDraft, phoneCountry);
    setCustomer(["phone"], sanitizePhone(nextPhone));
    setCustomer(["phoneCountry"], phoneCountry || "");
    setPhonePadOpen(false);
    setPhoneDraft("");
    setPhoneCountry("");
    setPhoneCountrySheetOpen(false);
  }

  function handlePhonePadInput(key) {
    if (key === "backspace") {
      setPhoneDraft((prev) => String(prev || "").slice(0, -1));
      return;
    }

    if (key === "clear") {
      setPhoneDraft("");
      return;
    }

    setPhoneDraft((prev) =>
      capNationalDigitsForCountry(
        String(`${String(prev || "")}${key}`).replace(/\D/g, ""),
        phoneCountry || getPreferredPhoneCountry(customer)
      )
    );
  }

  function openPhoneCountrySheet() {
    if (readOnly) return;
    setCountrySearch("");
    setPhoneCountrySheetOpen(true);
  }

  function closePhoneCountrySheet() {
    setPhoneCountrySheetOpen(false);
  }

  function applyPhoneCountry(countryCode) {
    const nextCountry = String(countryCode || "").toUpperCase();
    setPhoneCountry(nextCountry);
    setPhoneDraft((prev) => capNationalDigitsForCountry(prev, nextCountry));
    setPhoneCountrySheetOpen(false);
  }

  function applyDobSheet() {
    setCustomer(["dobOrIncorporationDate"], draftDob || null);
    setDobSheetOpen(false);
  }

  function handleDobCalendarChange(e) {
    const nextDate = e.value || null;

    if (!nextDate) {
      setDraftDob(null);
      return;
    }

    if (dobSelectionStage === "year") {
      setDobViewDate(nextDate);
      setDobSelectionStage("month");
      return;
    }

    if (dobSelectionStage === "month") {
      setDobViewDate(nextDate);
      setDobSelectionStage("date");
      return;
    }

    setDraftDob(nextDate);
    setDobViewDate(nextDate);
    setCustomer(["dobOrIncorporationDate"], nextDate);
    setDobSheetOpen(false);
  }

  function applyNationality(countryCode) {
    setCustomer(["nationalityOrIncorporationCountry"], countryCode || "");
    if (isEntity && !customer.country) {
      setCustomer(["country"], countryCode || "");
    }
    setNationalitySheetOpen(false);
  }

  function applyOperationCountry(countryCode) {
    setCustomer(["mainCountryOfOperation"], countryCode || "");
    setOperationCountrySheetOpen(false);
  }

  function applyCountry(countryCode) {
    setCustomer(["country"], countryCode || "");
    if (isEntity) {
      setCustomer(["nationalityOrIncorporationCountry"], countryCode || "");
    }
    setCountrySheetOpen(false);
  }

  function applyAddressSuggestion(item) {
    setCustomer(["address"], capitalizeFirstLetter(item.address || ""));
    if (item.city) setCustomer(["city"], capitalizeFirstLetter(item.city));
    if (item.country) {
      setCustomer(["country"], item.country);
      if (isEntity) {
        setCustomer(["nationalityOrIncorporationCountry"], item.country);
      }
    }
    setAddressSuggestions([]);
    setAddressDropdownOpen(false);
  }

  function applyCitySuggestion(item) {
    setCustomer(["city"], capitalizeFirstLetter(item.city || ""));
    if (item.country) {
      setCustomer(["country"], item.country);
      if (isEntity) {
        setCustomer(["nationalityOrIncorporationCountry"], item.country);
      }
    }
    setCitySuggestions([]);
    setCityDropdownOpen(false);
  }

  async function fetchAddressSuggestions(query) {
    if (!MAPBOX_TOKEN || readOnly) {
      setAddressSuggestions([]);
      setAddressLoading(false);
      return;
    }

    const trimmed = String(query || "").trim();
    if (trimmed.length < 3) {
      setAddressSuggestions([]);
      setAddressLoading(false);
      return;
    }

    if (addressAbortRef.current) {
      addressAbortRef.current.abort();
    }

    const controller = new AbortController();
    addressAbortRef.current = controller;
    setAddressLoading(true);

    try {
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          trimmed
        )}.json` +
        `?access_token=${encodeURIComponent(MAPBOX_TOKEN)}` +
        `&autocomplete=true&types=address,place,locality,neighborhood`;

      const res = await fetch(url, { signal: controller.signal });
      const json = await res.json().catch(() => ({}));
      const items = Array.isArray(json?.features)
        ? json.features.map(parseMapboxFeature).filter(Boolean)
        : [];
      setAddressSuggestions(items);
      setAddressDropdownOpen(items.length > 0);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setAddressSuggestions([]);
        setAddressDropdownOpen(false);
      }
    } finally {
      if (addressAbortRef.current === controller) {
        addressAbortRef.current = null;
      }
      setAddressLoading(false);
    }
  }

  async function fetchCitySuggestions(query) {
    if (!MAPBOX_TOKEN || readOnly) {
      setCitySuggestions([]);
      setCityLoading(false);
      return;
    }

    const trimmed = String(query || "").trim();
    if (trimmed.length < 2) {
      setCitySuggestions([]);
      setCityLoading(false);
      return;
    }

    if (cityAbortRef.current) {
      cityAbortRef.current.abort();
    }

    const controller = new AbortController();
    cityAbortRef.current = controller;
    setCityLoading(true);

    try {
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          trimmed
        )}.json` +
        `?access_token=${encodeURIComponent(MAPBOX_TOKEN)}` +
        `&autocomplete=true&types=place,locality,district`;

      const res = await fetch(url, { signal: controller.signal });
      const json = await res.json().catch(() => ({}));
      const items = Array.isArray(json?.features)
        ? json.features.map(parseMapboxCityFeature).filter(Boolean)
        : [];
      setCitySuggestions(items);
      setCityDropdownOpen(items.length > 0);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setCitySuggestions([]);
        setCityDropdownOpen(false);
      }
    } finally {
      if (cityAbortRef.current === controller) {
        cityAbortRef.current = null;
      }
      setCityLoading(false);
    }
  }

  function handleAddressInput(value) {
    setCustomer(["address"], capitalizeFirstLetter(value));

    if (addressTimerRef.current) {
      clearTimeout(addressTimerRef.current);
    }

    const trimmed = String(value || "").trim();
    if (!MAPBOX_TOKEN || trimmed.length < 3) {
      setAddressSuggestions([]);
      setAddressDropdownOpen(false);
      setAddressLoading(false);
      return;
    }

    addressTimerRef.current = setTimeout(() => {
      fetchAddressSuggestions(trimmed);
    }, 280);
  }

  function commitCustomAddress() {
    if (addressTimerRef.current) {
      clearTimeout(addressTimerRef.current);
      addressTimerRef.current = null;
    }
    if (addressAbortRef.current) {
      addressAbortRef.current.abort();
      addressAbortRef.current = null;
    }
    setAddressLoading(false);
    setAddressSuggestions([]);
    setAddressDropdownOpen(false);
  }

  function handleCityInput(value) {
    setCustomer(["city"], capitalizeFirstLetter(value));

    if (cityTimerRef.current) {
      clearTimeout(cityTimerRef.current);
    }

    const trimmed = String(value || "").trim();
    if (!MAPBOX_TOKEN || trimmed.length < 2) {
      setCitySuggestions([]);
      setCityDropdownOpen(false);
      setCityLoading(false);
      return;
    }

    cityTimerRef.current = setTimeout(() => {
      fetchCitySuggestions(trimmed);
    }, 240);
  }

  function commitCustomCity() {
    if (cityTimerRef.current) {
      clearTimeout(cityTimerRef.current);
      cityTimerRef.current = null;
    }
    if (cityAbortRef.current) {
      cityAbortRef.current.abort();
      cityAbortRef.current = null;
    }
    setCityLoading(false);
    setCitySuggestions([]);
    setCityDropdownOpen(false);
  }

  const nationalityLabel = isEntity
    ? "Country of incorporation"
    : "Nationality";
  const topCountryLabel = showMainCountryOfOperation
    ? "Main country of operation"
    : nationalityLabel;
  const topCountryCode = showMainCountryOfOperation
    ? customer.mainCountryOfOperation
    : customer.nationalityOrIncorporationCountry;
  const topCountryValue = countryCodeToName(topCountryCode) || "Choose";
  const dateLabel = isEntity ? "Date of incorporation" : "Date of birth";
  const nationalityValue =
    countryCodeToName(customer.nationalityOrIncorporationCountry) || "Choose";
  const dobValue = fmtDate(customer.dobOrIncorporationDate) || "Choose";
  const countryValue = countryCodeToName(customer.country) || "Choose";
  const emailValue = String(customer.email || "");
  const phoneValue = String(customer.phone || "");
  const websiteValue = String(customer.website || "");
  const activePhoneCountry = phoneCountry || getPreferredPhoneCountry(customer);
  const phoneCountryDisplay = countryCodeToName(activePhoneCountry) || "Choose";
  const phoneCallingCode = getPhoneCountryCode(activePhoneCountry);
  const phoneDisplayValue = formatPhoneDraft(phoneDraft, activePhoneCountry);
  const missingName = continueAttempted && !String(customer.name || "").trim();
  const missingNationality =
    continueAttempted &&
    !isEntity &&
    !String(customer.nationalityOrIncorporationCountry || "").trim();
  const missingMainCountryOfOperation =
    continueAttempted &&
    showMainCountryOfOperation &&
    !String(customer.mainCountryOfOperation || "").trim();
  const missingDob =
    continueAttempted && !toDateOrNull(customer.dobOrIncorporationDate);
  const missingAddress =
    continueAttempted && !String(customer.address || "").trim();
  const missingCity = continueAttempted && !String(customer.city || "").trim();
  const missingCountry =
    continueAttempted && !String(customer.country || "").trim();
  const missingEmail = continueAttempted && !emailValue.trim();
  const missingPhone = continueAttempted && !String(customer.phone || "").trim();
  const missingBusinessActivity =
    continueAttempted && !String(customer.businessActivity || "").trim();
  const typedAddress = String(customer.address || "").trim();
  const typedCity = String(customer.city || "").trim();
  const addressOptions = typedAddress
    ? [
        {
          id: "__custom_address__",
          address: typedAddress,
          label: typedAddress,
          isCustomValue: true,
        },
        ...addressSuggestions.filter((item) => {
          const itemValue = String(item.address || item.label || "").trim();
          return itemValue.toLowerCase() !== typedAddress.toLowerCase();
        }),
      ]
    : addressSuggestions;
  const cityOptions = typedCity
    ? [
        {
          id: "__custom_city__",
          city: typedCity,
          label: typedCity,
          isCustomValue: true,
        },
        ...citySuggestions.filter((item) => {
          const itemPrimaryValue = String(item.city || item.label || "").trim();
          const itemLabelValue = String(item.label || itemPrimaryValue).trim();
          const typedCityLower = typedCity.toLowerCase();
          return !(
            itemPrimaryValue.toLowerCase() === typedCityLower &&
            itemLabelValue.toLowerCase() === typedCityLower
          );
        }),
      ]
    : citySuggestions;
  const showEmailError =
    (emailTouched || continueAttempted) &&
    emailValue.trim() !== "" &&
    !isValidEmail(emailValue);
  const showWebsiteError =
    websiteTouched &&
    websiteValue.trim() !== "" &&
    !isValidWebsite(websiteValue);
  const filteredCountryOptions = countryOptions.filter((opt) =>
    countryMatchesSearch(opt, countrySearch)
  );

  return (
    <div className="kycMobileSectionCard">
      <div className="kycMobileSectionStack">
        <div className="kycMobileIdentityHeroCard">
          <div className="kycMobileIdentityHeroIcon">
            <Lottie
              animationData={isEntity ? iconEntityNameJson : iconNameJson}
              loop
              autoplay
              className="kycMobileIdentityHeroLottie"
            />
          </div>
          <div className="kycMobileIdentityHeroCopy">
            <div className="kycMobileIdentityHeroTitle">
              {isEntity ? "Legal name" : "Full name"}
            </div>
            {separateNames && !isEntity && !readOnly ? (
              <MobileIndividualNamePreview value={customer} displayName={customer.name || ""} onChange={(patch) => onSetAt(["customer"], { ...customer, ...patch })} />
            ) : (
            <input
              {...MOBILE_TEXT_ENTRY_PROPS}
              className="form-control"
              value={customer.name || ""}
              onChange={(e) => setCustomer(["name"], e.target.value)}
              disabled={readOnly}
            />
            )}
            {missingName ? (
              <RequiredFieldError />
            ) : null}
          </div>
        </div>

        <div className="kycMobilePickerRow">
          {!isEntity || showMainCountryOfOperation ? (
            <div className="kycMobilePickerCard">
              <div className="kycMobilePickerRail">
                <img src={iconPassport} alt="" className="kycMobilePickerIcon" />
              </div>
              <div className="kycMobilePickerContent">
                <div className="kycMobilePickerLabel">{topCountryLabel}</div>
                <button
                  type="button"
                  className="kycMobilePickerButton"
                  onClick={
                    showMainCountryOfOperation
                      ? openOperationCountrySheet
                      : openNationalitySheet
                  }
                  disabled={readOnly}
                >
                  <span className="kycMobilePickerButtonValue">
                    {topCountryCode ? (
                      <CircleFlag
                        className="kycMobilePickerFlag"
                        countryCode={String(topCountryCode || "").toLowerCase()}
                        height="18"
                      />
                    ) : null}
                    <span>{topCountryValue}</span>
                  </span>
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                </button>
                {(showMainCountryOfOperation
                  ? missingMainCountryOfOperation
                  : missingNationality) ? (
                  <RequiredFieldError />
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="kycMobilePickerCard">
            <div className="kycMobilePickerRail">
              <img
                src={iconDateOfBirth}
                alt=""
                className="kycMobilePickerIcon"
              />
            </div>
            <div className="kycMobilePickerContent">
              <div className="kycMobilePickerLabel">{dateLabel}</div>
              <button
                type="button"
                className="kycMobilePickerButton"
                onClick={openDobSheet}
                disabled={readOnly}
              >
                <span>{dobValue}</span>
                <i className="bi bi-chevron-right" aria-hidden="true" />
              </button>
              {missingDob ? (
                <RequiredFieldError />
              ) : null}
            </div>
          </div>
        </div>

        <div className="kycMobilePickerCard" ref={addressBoxRef}>
          <div className="kycMobilePickerRail">
            <img src={iconAddress} alt="" className="kycMobilePickerIcon" />
          </div>
          <div className="kycMobilePickerContent">
            <div className="kycMobilePickerSectionEyebrow">
              {isEntity ? "REGISTERED OFFICEE" : "Domicile"}
            </div>
            <div className="kycMobileSectionStack">
              <div>
                <label className="kycMobileFieldLabel">
                  Address
                </label>
                <input
                  {...MOBILE_TEXT_ENTRY_PROPS}
                  className="form-control"
                  value={customer.address || ""}
                  onChange={(e) => handleAddressInput(e.target.value)}
                  onFocus={() => {
                    if (addressSuggestions.length > 0) {
                      setAddressDropdownOpen(true);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitCustomAddress();
                      e.currentTarget.blur();
                    }
                  }}
                  onBlur={() => {
                    window.setTimeout(() => {
                      commitCustomAddress();
                    }, 120);
                  }}
                  disabled={readOnly}
                />
                {missingAddress ? (
                  <RequiredFieldError />
                ) : null}
                {MAPBOX_TOKEN ? (
                  <>
                    {addressLoading ? (
                      <div className="kycMobileAddressStatus">Searching address…</div>
                    ) : null}
                    {addressDropdownOpen && addressOptions.length > 0 ? (
                      <div className="kycMobileAddressDropdown">
                        {addressOptions.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className={`kycMobileAddressOption${
                              item.isCustomValue ? " kycMobileAddressOptionCustom" : ""
                            }`}
                            onClick={() => {
                              if (item.isCustomValue) {
                                commitCustomAddress();
                                return;
                              }
                              applyAddressSuggestion(item);
                            }}
                          >
                            <span className="kycMobileAddressOptionMain">
                              {item.address || item.label}
                            </span>
                            <span className="kycMobileAddressOptionMeta">
                              {item.isCustomValue
                                ? "Use entered address"
                                : [item.city, countryCodeToName(item.country)]
                                    .filter(Boolean)
                                    .join(", ") || item.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>

              <div ref={cityBoxRef}>
                <label className="kycMobileFieldLabel">City</label>
                <input
                  {...MOBILE_TEXT_ENTRY_PROPS}
                  className="form-control"
                  value={customer.city || ""}
                  onChange={(e) => handleCityInput(e.target.value)}
                  onFocus={() => {
                    if (citySuggestions.length > 0) {
                      setCityDropdownOpen(true);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitCustomCity();
                      e.currentTarget.blur();
                    }
                  }}
                  onBlur={() => {
                    window.setTimeout(() => {
                      commitCustomCity();
                    }, 120);
                  }}
                  disabled={readOnly}
                />
                {missingCity ? (
                  <RequiredFieldError />
                ) : null}
                {MAPBOX_TOKEN ? (
                  <>
                    {cityLoading ? (
                      <div className="kycMobileAddressStatus">Searching city…</div>
                    ) : null}
                    {cityDropdownOpen && cityOptions.length > 0 ? (
                      <div className="kycMobileAddressDropdown">
                        {cityOptions.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className={`kycMobileAddressOption${
                              item.isCustomValue ? " kycMobileAddressOptionCustom" : ""
                            }`}
                            onClick={() => {
                              if (item.isCustomValue) {
                                commitCustomCity();
                                return;
                              }
                              applyCitySuggestion(item);
                            }}
                          >
                            <span className="kycMobileAddressOptionMain">
                              {item.city}
                            </span>
                            <span className="kycMobileAddressOptionMeta">
                              {item.isCustomValue
                                ? "Use entered city"
                                : countryCodeToName(item.country) || item.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>

              <div>
                <label className="kycMobileFieldLabel">
                  {isEntity ? "Country of incorporation" : "Country"}
                </label>
                <button
                  type="button"
                  className="kycMobilePickerButton"
                  onClick={openCountrySheet}
                  disabled={readOnly}
                >
                  <span className="kycMobilePickerButtonValue">
                    {customer.country ? (
                      <CircleFlag
                        className="kycMobilePickerFlag"
                        countryCode={String(customer.country || "").toLowerCase()}
                        height="18"
                      />
                    ) : null}
                    <span>{countryValue}</span>
                  </span>
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                </button>
                {missingCountry ? (
                  <RequiredFieldError />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="kycMobilePickerCard">
          <div className="kycMobilePickerRail">
            <img src={iconEmail} alt="" className="kycMobilePickerIcon" />
          </div>
          <div className="kycMobilePickerContent">
            <label className="kycMobileFieldLabel">Email</label>
            <input
              {...MOBILE_TEXT_ENTRY_PROPS}
              className="form-control"
              type="email"
              value={emailValue}
              onChange={(e) => setCustomer(["email"], e.target.value)}
              onBlur={() => setEmailTouched(true)}
              disabled={readOnly}
            />
            {missingEmail ? (
              <RequiredFieldError />
            ) : showEmailError ? (
              <div className="kycMobileFieldError">
                <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
                Please enter a valid email address
              </div>
            ) : null}
          </div>
        </div>

        <div className="kycMobilePickerCard">
          <div className="kycMobilePickerRail">
            <img src={iconPhoneCall} alt="" className="kycMobilePickerIcon" />
          </div>
          <div className="kycMobilePickerContent">
            <label className="kycMobileFieldLabel">Phone number</label>
            <button
              type="button"
              className={`form-control kycMobileInputTrigger ${
                phoneValue ? "" : "is-placeholder"
              }`}
              onClick={openPhonePad}
              disabled={readOnly}
            >
              <span>{phoneValue || "Enter phone number"}</span>
            </button>
            {missingPhone ? (
              <RequiredFieldError />
            ) : null}
          </div>
        </div>

        {isEntity ? (
          <div className="kycMobileFieldCard">
            <label className="kycMobileFieldLabel">Website (optional)</label>
            <input
              {...MOBILE_TEXT_ENTRY_PROPS}
              className="form-control"
              value={websiteValue}
              onChange={(e) => setCustomer(["website"], e.target.value)}
              onBlur={() => setWebsiteTouched(true)}
              disabled={readOnly}
            />
            {showWebsiteError ? (
              <div className="kycMobileFieldError">
                Please enter a valid website address.
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="kycMobileFieldCard kycMobileFieldCard--large">
          <div className="kycMobileFieldLabelWithIcon kycMobileFieldLabelWithIcon--spaced">
            <img
              src={iconBusinessActivity}
              alt=""
              className="kycMobileFieldIcon kycMobileFieldIcon--large"
            />
            <label className="kycMobileFieldLabel kycMobileFieldLabel--large mb-0">
              Business activity
            </label>
          </div>
          <textarea
            {...MOBILE_TEXT_ENTRY_PROPS}
            ref={businessActivityRef}
            className="form-control kycMobileAutoGrowTextarea"
            rows={3}
            style={{
              resize: "none",
              minHeight: BUSINESS_ACTIVITY_MIN_HEIGHT,
            }}
            value={customer.businessActivity || ""}
            onChange={(e) => {
              const val = capitalizeFirstLetter(e.target.value);
              autoResizeTextarea(e.currentTarget);
              setCustomer(["businessActivity"], val);
              debouncedSyncIndustryAssessment(val);
            }}
            onInput={(e) => autoResizeTextarea(e.currentTarget)}
            disabled={readOnly}
          />
          {missingBusinessActivity ? (
            <RequiredFieldError />
          ) : null}
          <div className="kycMobileFieldHint">
            {isEntity
              ? "Describe the main business activity of the company including the nature of its operations and the main products or services provided."
              : "Describe the main professional, business, or other income-generating activity from which the source of income is derived."}
          </div>
        </div>
      </div>

      {typeof document !== "undefined"
        ? createPortal(
            <AnimatePresence>
              {nationalitySheetOpen ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeNationalitySheet}
                  />

                  <motion.div
                    className="kycMobileCountrySheet"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileCountrySheetHeader">
                      <button
                        type="button"
                        className="kycMobileDateSheetBtn kycMobileDateSheetBtn--ghost"
                        onClick={closeNationalitySheet}
                      >
                        Cancel
                      </button>
                      <div className="kycMobileDateSheetTitle">
                        {nationalityLabel}
                      </div>
                      <div className="kycMobileCountrySheetSpacer" />
                    </div>

                    <div className="kycMobileCountrySearchWrap">
                      <i className="bi bi-search" aria-hidden="true" />
                      <input
                        {...MOBILE_TEXT_ENTRY_PROPS}
                        ref={countrySearchRef}
                        autoFocus
                        className="kycMobileCountrySearchInput"
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            filteredCountryOptions.length === 1
                          ) {
                            e.preventDefault();
                            applyNationality(filteredCountryOptions[0].value);
                          }
                        }}
                        placeholder="Search country"
                      />
                    </div>

                    <div className="kycMobileCountryList">
                      {filteredCountryOptions.map((opt) => {
                        const isSelected =
                          opt.value === customer.nationalityOrIncorporationCountry;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`kycMobileCountryOption ${
                              isSelected ? "is-selected" : ""
                            }`}
                            onClick={() => applyNationality(opt.value)}
                          >
                            <span className="kycMobileCountryOptionLeft">
                              <CircleFlag
                                className="kycMobilePickerFlag"
                                countryCode={String(opt.value || "").toLowerCase()}
                                height="18"
                              />
                              <span>{opt.label}</span>
                            </span>
                            {isSelected ? (
                              <i className="bi bi-check2" aria-hidden="true" />
                            ) : (
                              <i
                                className="bi bi-chevron-right"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                </>
              ) : null}

              {operationCountrySheetOpen ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeOperationCountrySheet}
                  />

                  <motion.div
                    className="kycMobileCountrySheet"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileCountrySheetHeader">
                      <button
                        type="button"
                        className="kycMobileDateSheetBtn kycMobileDateSheetBtn--ghost"
                        onClick={closeOperationCountrySheet}
                      >
                        Cancel
                      </button>
                      <div className="kycMobileDateSheetTitle">
                        Main country of operation
                      </div>
                      <div className="kycMobileCountrySheetSpacer" />
                    </div>

                    <div className="kycMobileCountrySearchWrap">
                      <i className="bi bi-search" aria-hidden="true" />
                      <input
                        {...MOBILE_TEXT_ENTRY_PROPS}
                        ref={countrySearchRef}
                        autoFocus
                        className="kycMobileCountrySearchInput"
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            filteredCountryOptions.length === 1
                          ) {
                            e.preventDefault();
                            applyOperationCountry(filteredCountryOptions[0].value);
                          }
                        }}
                        placeholder="Search country"
                      />
                    </div>

                    <div className="kycMobileCountryList">
                      {filteredCountryOptions.map((opt) => {
                        const isSelected =
                          opt.value === customer.mainCountryOfOperation;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`kycMobileCountryOption ${
                              isSelected ? "is-selected" : ""
                            }`}
                            onClick={() => applyOperationCountry(opt.value)}
                          >
                            <span className="kycMobileCountryOptionLeft">
                              <CircleFlag
                                className="kycMobilePickerFlag"
                                countryCode={String(opt.value || "").toLowerCase()}
                                height="18"
                              />
                              <span>{opt.label}</span>
                            </span>
                            {isSelected ? (
                              <i className="bi bi-check2" aria-hidden="true" />
                            ) : (
                              <i
                                className="bi bi-chevron-right"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                </>
              ) : null}

              {countrySheetOpen ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeCountrySheet}
                  />

                  <motion.div
                    className="kycMobileCountrySheet"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileCountrySheetHeader">
                      <button
                        type="button"
                        className="kycMobileDateSheetBtn kycMobileDateSheetBtn--ghost"
                        onClick={closeCountrySheet}
                      >
                        Cancel
                      </button>
                      <div className="kycMobileDateSheetTitle">Country</div>
                      <div className="kycMobileCountrySheetSpacer" />
                    </div>

                    <div className="kycMobileCountrySearchWrap">
                      <i className="bi bi-search" aria-hidden="true" />
                      <input
                        {...MOBILE_TEXT_ENTRY_PROPS}
                        ref={countrySearchRef}
                        autoFocus
                        className="kycMobileCountrySearchInput"
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            filteredCountryOptions.length === 1
                          ) {
                            e.preventDefault();
                            applyCountry(filteredCountryOptions[0].value);
                          }
                        }}
                        placeholder="Search country"
                      />
                    </div>

                    <div className="kycMobileCountryList">
                      {filteredCountryOptions.map((opt) => {
                        const isSelected = opt.value === customer.country;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`kycMobileCountryOption ${
                              isSelected ? "is-selected" : ""
                            }`}
                            onClick={() => applyCountry(opt.value)}
                          >
                            <span className="kycMobileCountryOptionLeft">
                              <CircleFlag
                                className="kycMobilePickerFlag"
                                countryCode={String(opt.value || "").toLowerCase()}
                                height="18"
                              />
                              <span>{opt.label}</span>
                            </span>
                            {isSelected ? (
                              <i className="bi bi-check2" aria-hidden="true" />
                            ) : (
                              <i
                                className="bi bi-chevron-right"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                </>
              ) : null}

              {phoneCountrySheetOpen ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop kycMobilePhoneCountryBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closePhoneCountrySheet}
                  />

                  <div className="kycMobilePhoneCountryLayer">
                    <motion.div
                      className="kycMobileCountrySheet kycMobilePhoneCountrySheet"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{ duration: 0.18 }}
                    >
                      <div className="kycMobileCountrySheetHeader">
                        <button
                          type="button"
                          className="kycMobileDateSheetBtn kycMobileDateSheetBtn--ghost"
                          onClick={closePhoneCountrySheet}
                        >
                          Cancel
                        </button>
                        <div className="kycMobileDateSheetTitle">
                          Phone country
                        </div>
                        <div className="kycMobileCountrySheetSpacer" />
                      </div>

                      <div className="kycMobileCountrySearchWrap">
                        <i className="bi bi-search" aria-hidden="true" />
                        <input
                          {...MOBILE_TEXT_ENTRY_PROPS}
                          ref={countrySearchRef}
                          autoFocus
                          className="kycMobileCountrySearchInput"
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" &&
                              filteredCountryOptions.length === 1
                            ) {
                              e.preventDefault();
                              applyPhoneCountry(filteredCountryOptions[0].value);
                            }
                          }}
                          placeholder="Search country"
                        />
                      </div>

                      <div className="kycMobileCountryList">
                        {filteredCountryOptions.map((opt) => {
                          const isSelected = opt.value === activePhoneCountry;
                          const dialCode = getPhoneCountryCode(opt.value);
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              className={`kycMobileCountryOption ${
                                isSelected ? "is-selected" : ""
                              }`}
                              onClick={() => applyPhoneCountry(opt.value)}
                            >
                              <span className="kycMobileCountryOptionLeft">
                                <CircleFlag
                                  className="kycMobilePickerFlag"
                                  countryCode={String(opt.value || "").toLowerCase()}
                                  height="18"
                                />
                                <span>
                                  {opt.label}
                                  {dialCode ? ` (${dialCode})` : ""}
                                </span>
                              </span>
                              {isSelected ? (
                                <i className="bi bi-check2" aria-hidden="true" />
                              ) : (
                                <i
                                  className="bi bi-chevron-right"
                                  aria-hidden="true"
                                />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  </div>
                </>
              ) : null}

              {dobSheetOpen ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeDobSheet}
                  />

                  <motion.div
                    className="kycMobileDateSheet"
                    initial={{ opacity: 0, scale: 0.96, y: 24 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 24 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileDateSheetHandle" />

                    <div className="kycMobileDateSheetHeader">
                      <button
                        type="button"
                        className="kycMobileDateSheetBtn kycMobileDateSheetBtn--ghost"
                        onClick={closeDobSheet}
                      >
                        Cancel
                      </button>
                      <div className="kycMobileDateSheetTitle">{dateLabel}</div>
                      <div className="kycMobileCountrySheetSpacer" />
                    </div>

                    <div className="kycMobileDateSheetBody">
                      <Calendar
                        key={`dob-${dobSelectionStage}`}
                        value={draftDob}
                        onChange={handleDobCalendarChange}
                        onViewDateChange={(e) =>
                          setDobViewDate(e.value || draftDob || new Date())
                        }
                        inline
                        showIcon={false}
                        dateFormat="d M yy"
                        maxDate={isEntity ? null : new Date()}
                        className="kycMobileInlineCalendar"
                        view={dobSelectionStage}
                        viewDate={dobViewDate}
                      />
                    </div>
                  </motion.div>
                </>
              ) : null}

              {phonePadOpen ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closePhonePad}
                  />

                  <motion.div
                    className="kycMobilePhoneModal"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 18 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobilePhoneModalTop">
                      <div className="kycMobilePhoneModalTopBar">
                        <div className="kycMobilePhoneModalTitle">
                          <img
                            src={iconPhoneBlue}
                            alt=""
                            className="kycMobilePhoneModalTitleIcon"
                          />
                          PHONE NUMBER
                        </div>
                        <button
                          type="button"
                          className="kycMobileOwnershipModalClose"
                          onClick={closePhonePad}
                          aria-label="Close"
                        >
                          <i className="bi bi-x-lg" aria-hidden="true" />
                        </button>
                      </div>

                      <button
                        type="button"
                        className="kycMobilePhoneCountryTrigger"
                        onClick={openPhoneCountrySheet}
                      >
                        <span className="kycMobilePhoneCountryTriggerLeft">
                          <CircleFlag
                            className="kycMobilePickerFlag"
                            countryCode={String(activePhoneCountry || "ae").toLowerCase()}
                            height="18"
                          />
                          <span>{phoneCountryDisplay}</span>
                        </span>
                        <span className="kycMobilePhoneCountryTriggerCode">
                          {phoneCallingCode}
                        </span>
                      </button>

                      <div
                        className={`kycMobilePhoneModalValue ${
                          phoneDraft ? "" : "is-placeholder"
                        }`}
                      >
                        {phoneDisplayValue || "Enter phone number"}
                      </div>
                    </div>

                    <div className="kycMobilePhoneModalBottom">
                      <div className="kycMobilePhoneKeypad">
                        {[
                          "1",
                          "2",
                          "3",
                          "4",
                          "5",
                          "6",
                          "7",
                          "8",
                          "9",
                          "clear",
                          "0",
                          "backspace",
                        ].map((key) => (
                          <button
                            key={key}
                            type="button"
                            className="kycMobilePhoneKey"
                            onClick={() => handlePhonePadInput(key)}
                          >
                            {key === "backspace" ? (
                              <i className="bi bi-backspace" aria-hidden="true" />
                            ) : key === "clear" ? (
                              <i
                                className="bi bi-arrow-counterclockwise"
                                aria-hidden="true"
                              />
                            ) : (
                              key
                            )}
                          </button>
                        ))}
                      </div>

                      <button
                        type="button"
                        className="kycMobilePhoneValidateButton"
                        onClick={applyPhonePad}
                      >
                        Validate
                      </button>
                    </div>
                  </motion.div>
                </>
              ) : null}
            </AnimatePresence>,
            document.body
          )
        : null}
    </div>
  );
}
